import * as d3 from 'd3';
import * as _ from 'underscore';

import * as selectioncontextmenu from 'components/SelectionContextMenu';
import {MAX_VISIBLE_RING_FEATURES} from 'constants/MapsConstants';
import Annotation from 'molbio/annotations/backbone/Annotation';
import {RingLayout} from 'molbio/maps/circular/CircularLayout';
import {getArcForStrand} from 'molbio/maps/circular/CircularUtil';
import LabeledSegmentsRingView from 'molbio/maps/circular/LabeledSegmentsRingView';

class CircularAnnotationsView extends LabeledSegmentsRingView<RingLayout, Annotation> {
  override className!: string;
  singularClassName!: string;
  override tagName!: string;
  override rings?: number | null;

  static initClass(): void {
    this.prototype.className = 'annotations';
    this.prototype.singularClassName = 'annotation';
    this.prototype.tagName = 'g';
  }

  override contextMenuData(): Array<string> {
    return selectioncontextmenu.ANNOTATION_MENU;
  }

  override contextMenuOptions(d: Annotation): {annotation: Annotation} {
    return {annotation: d};
  }

  override arc(d: Annotation): d3.Arc<unknown, d3.DefaultArcObject> {
    return getArcForStrand(d.strand());
  }

  override data(): Array<Annotation> {
    const sequence = this.model.get('sequence');
    // NOTE: For smart rendering, this works, but it look silly.
    let visible: Array<Annotation> = [];
    const total = sequence.annotations();
    const shown = total.slice(
      this.model.get('leftBP'),
      this.model.get('rightBP'),
      (data: Array<Annotation>) => {
        visible = data;
        return _.sortBy(data, (a: Annotation) => -a.length(sequence)).slice(0, MAX_VISIBLE_RING_FEATURES);
      }
    );

    this.model.set({totalA: total.length, visibleA: visible.length, shownA: shown.length});
    return shown;
  }

  override hideHandler(): void {
    if (!this.layout.get('visible')) {
      this.model.set({visibleA: 0, shownA: 0});
    }
  }

  clickAction(e: MouseEvent, d: Annotation): void {
    d.click(this.model.get('sequence'), e.shiftKey);
  }

  dblClickAction(e: MouseEvent, d: Annotation): void {
    d.dblClick(this.model.get('sequence'), e.shiftKey);
  }

  override registerListeners(): void {
    super.registerListeners();
    const annotations = this.model.get('sequence').annotations();
    this.listenTo(annotations, 'layerChange', function () {
      // @ts-expect-error: unbound this
      this.invalidateData();
      // @ts-expect-error: unbound this
      this.triggerRedraw();
    });
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.listenTo(annotations, 'change:hover change:selected', this.handleHover);
  }

  override setConstraints(outerRadius: number): void {
    this.setOuterRadius(outerRadius);
  }

  override redrawPlasmid(): boolean {
    return true;
  }

  override storeBounds(rings?: number | null): void {
    if (rings == null) {
      ({rings} = this);
    }
    this.rings = rings!;
    const innerRadius = this.getOuterRadius() - this.layout.get('ringHeight') * this.rings;
    this.layout.set('innerRadius', innerRadius);
    this.updateRadius();
  }

  override zeroBounds(): void {
    this.layout.set('innerRadius', this.layout.get('outerRadius'));
    this.updateRadius();
  }

  override layerBounds(layer: number): {innerRadius: number; outerRadius: number} {
    return {
      innerRadius: this.radiusIn(layer + 1),
      outerRadius: this.radiusIn(layer) - this.layout.vMargin(),
    };
  }

  override baseLine(layer: number, innerRadius?: number | null): number {
    return this.radiusIn(layer + 1);
  }
}
CircularAnnotationsView.initClass();

export default CircularAnnotationsView;
